import React, { useEffect, useState } from 'react';
import { sessionApi } from '../../services/api';
import {
  UserSession,
  appLabel,
  fmtDateTimeIST,
  statusLabel,
} from './loginActivity';

const TONE_CLASSES: Record<string, string> = {
  active: 'bg-emerald-50 text-emerald-700 border-emerald-200',
  closed: 'bg-slate-50 text-slate-600 border-slate-200',
  stale: 'bg-amber-50 text-amber-700 border-amber-200',
};

/**
 * Recent logins for one user — device, app, login time, logout time, status.
 * The backend caps the list at 10; this is a rolling recent-activity view,
 * not a full audit log.
 */
const LoginActivityPanel: React.FC<{ mobile: string }> = ({ mobile }) => {
  const [sessions, setSessions] = useState<UserSession[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    sessionApi
      .getUserSessions(mobile)
      .then((rows) => { if (!cancelled) setSessions(rows); })
      .catch(() => { if (!cancelled) setError('Could not load login activity.'); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [mobile]);

  return (
    <div className="bg-white rounded-xl border border-slate-200 p-4 lg:p-6">
      <h2 className="text-sm font-bold text-slate-600 uppercase tracking-wider mb-3">
        Login Activity
      </h2>

      {loading && <p className="text-[13px] text-slate-500">Loading…</p>}
      {!loading && error && <p className="text-[13px] text-rose-600">{error}</p>}
      {!loading && !error && sessions.length === 0 && (
        <p className="text-[13px] text-slate-500">No login activity recorded yet.</p>
      )}

      {!loading && !error && sessions.length > 0 && (
        <div className="overflow-x-auto">
          <table className="min-w-full">
            <thead className="bg-slate-50 border-b-2 border-slate-200">
              <tr>
                {['Device', 'App', 'Login', 'Logout', 'Status'].map((h) => (
                  <th
                    key={h}
                    className="px-4 py-3 text-left text-[11px] font-semibold text-slate-500 uppercase tracking-wider whitespace-nowrap"
                  >
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {sessions.map((s) => {
                const label = statusLabel(s.status);
                return (
                  <tr key={s.session_id}>
                    <td className="px-4 py-3 text-[13px] text-slate-700 whitespace-nowrap">
                      {s.device_model || '—'}
                      {s.app_version ? (
                        <span className="text-slate-400"> · v{s.app_version}</span>
                      ) : null}
                    </td>
                    <td className="px-4 py-3 text-[13px] text-slate-600 whitespace-nowrap">
                      {appLabel(s.app_name)}
                    </td>
                    <td className="px-4 py-3 text-[13px] text-slate-600 whitespace-nowrap">
                      {fmtDateTimeIST(s.login_at)}
                    </td>
                    <td className="px-4 py-3 text-[13px] text-slate-600 whitespace-nowrap">
                      {fmtDateTimeIST(s.logout_at)}
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap">
                      <span
                        className={`inline-block px-2 py-1 rounded-md border text-[11px] font-semibold ${
                          TONE_CLASSES[label.tone]
                        }`}
                      >
                        {label.text}
                      </span>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default LoginActivityPanel;
